import { useState, useRef, useEffect } from 'react';
import { MessageSquare, Send, Bot, Paperclip, ChevronDown, Maximize2, Minimize2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/lib/auth';
import { useRole } from '@/lib/role';

type Message = {
  id: string;
  sender: 'ai' | 'user';
  text: string;
  options?: string[];
  timestamp: Date;
  isTyping?: boolean;
};

interface AIChatWidgetProps {
  onCreateTicket?: (ticket: any) => void;
  tickets?: any[];
}

export function AIChatWidget({ onCreateTicket, tickets }: AIChatWidgetProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [flowState, setFlowState] = useState<'idle' | 'category' | 'title' | 'description' | 'upload' | 'priority' | 'preview' | 'check_status' | 'done'>('idle');
  const [ticketData, setTicketData] = useState<any>({});
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { session } = useAuth();
  const { role } = useRole();

  let userName = 'Guest';
  let isGuest = true;

  if (session?.user) {
    isGuest = false;
    const meta = (session.user as any).user_metadata || {};
    userName = meta.full_name || meta.display_name || meta.first_name || session.user.email?.split('@')[0] || 'User';
  }

  const getGreetingTime = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 18) return 'Good Afternoon';
    return 'Good Evening';
  };

  useEffect(() => {
    if (isOpen) {
      if (isGuest) {
        setMessages([
          {
            id: `msg-${Date.now()}`,
            sender: 'ai',
            text: 'Hello Guest 👋\nPlease sign in to create and manage support tickets.',
            options: ['Sign In'],
            timestamp: new Date()
          }
        ]);
      } else {
        const timeGreeting = getGreetingTime();
        const userOptions = (role === 'Super Admin' || role === 'Admin')
          ? ['Create Ticket', 'Assign Ticket', 'View All Tickets']
          : ['Create New Ticket', 'Check Ticket Status'];
          
        setMessages([
          {
            id: `msg-${Date.now()}`,
            sender: 'ai',
            text: `${timeGreeting} ${userName} 👋\nHow can I help you today?`,
            options: userOptions,
            timestamp: new Date()
          }
        ]);
      }
      setFlowState('idle');
      setUploadedFiles([]);
    } else {
      setMessages([]);
    }
  }, [isOpen, session?.user?.id, isGuest, userName, role]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const appendAiMessage = (text: string, options?: string[], delayMs = 1000) => {
    const typingId = `typing-${Date.now()}`;
    setMessages(prev => [...prev, { id: typingId, sender: 'ai', text: '', timestamp: new Date(), isTyping: true }]);
    
    setTimeout(() => {
      setMessages(prev => prev.filter(m => m.id !== typingId).concat({
        id: `msg-${Date.now()}`,
        sender: 'ai',
        text,
        options,
        timestamp: new Date()
      }));
    }, delayMs);
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    
    setMessages(prev => [...prev, {
      id: `msg-${Date.now()}`,
      sender: 'user',
      text: `📎 Attached: ${files.map(f => f.name).join(', ')}`,
      timestamp: new Date()
    }]);

    setUploadedFiles(prev => [...prev, ...files]);
    
    if (flowState === 'upload') {
      setFlowState('priority');
      appendAiMessage(`Files received. What is the priority of this issue?`, ['Low', 'Medium', 'High', 'Critical'], 1000);
    }
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSend = async (text: string = inputValue) => {
    if (!text.trim()) return;
    
    setMessages(prev => [...prev, {
      id: `msg-${Date.now()}`,
      sender: 'user',
      text,
      timestamp: new Date()
    }]);
    setInputValue('');

    if (flowState === 'idle') {
      if (text === 'Sign In') {
        appendAiMessage('Please use the "sign in" button at the top right of the application header to authenticate.');
      } else if (isGuest) {
        appendAiMessage('You must be signed in to perform this action.');
      } else if (text === 'Check Ticket Status') {
        setFlowState('check_status');
        appendAiMessage('Sure! Please provide the Ticket Number (e.g., INC-1234) you want to check.');
      } else if (text === 'Create New Ticket' || text === 'Create Ticket') {
        setFlowState('category');
        setTicketData({});
        appendAiMessage('I can help you with that. What is the category of your issue?', ['Hardware', 'Software', 'Network', 'Access/Login', 'Other']);
      } else {
        appendAiMessage('Would you like me to help you create a ticket?', ['Create New Ticket', 'Check Ticket Status']);
      }
    } 
    else if (flowState === 'check_status') {
      setFlowState('idle');
      if (tickets && tickets.length > 0) {
        const foundTicket = tickets.find(t => t.ref?.toLowerCase() === text.trim().toLowerCase());
        if (foundTicket) {
          appendAiMessage(`I checked the status for **${foundTicket.ref}**.\n\n**Status:** ${foundTicket.state}\n**Assigned To:** ${foundTicket.assignee || 'Unassigned'}`, ['Create New Ticket', 'Check Ticket Status']);
        } else {
          appendAiMessage(`I couldn't find a ticket matching **${text}**. Please check the reference number and try again.`, ['Create New Ticket', 'Check Ticket Status']);
        }
      } else {
        appendAiMessage(`I couldn't find any tickets matching **${text}**.`, ['Create New Ticket', 'Check Ticket Status']);
      }
    }
    else if (flowState === 'category') {
      setTicketData({ ...ticketData, category: text });
      setFlowState('title');
      appendAiMessage('Got it. Please provide a brief title for the issue.');
    }
    else if (flowState === 'title') {
      setTicketData({ ...ticketData, title: text });
      setFlowState('description');
      appendAiMessage('Please provide a detailed description of the problem.');
    }
    else if (flowState === 'description') {
      setTicketData({ ...ticketData, description: text });
      setFlowState('upload');
      appendAiMessage('Would you like to upload any documents or images related to this issue?', ['Upload File', 'Skip Upload']);
    }
    else if (flowState === 'upload' && text === 'Skip Upload') {
      setFlowState('priority');
      appendAiMessage('What is the priority of this issue?', ['Low', 'Medium', 'High', 'Critical']);
    }
    else if (flowState === 'priority') {
      setTicketData({ ...ticketData, priority: text });
      setFlowState('preview');
      
      const previewText = `Great, I have all the details. Here is a summary of your ticket:\n\n**Category:** ${ticketData.category}\n**Title:** ${ticketData.title}\n**Description:** ${ticketData.description}\n**Priority:** ${text}\n**Attachments:** ${uploadedFiles.length > 0 ? uploadedFiles.length + ' file(s)' : 'None'}\n\nShould I submit this ticket now?`;
      appendAiMessage(previewText, ['Submit Ticket', 'Edit Details'], 1000);
    }
    else if (flowState === 'preview' && text === 'Submit Ticket') {
      setFlowState('done');
      
      let assignedRef = '';
      if (onCreateTicket) {
        try {
          const res: any = await onCreateTicket({
            subject: ticketData.title || 'Support Ticket',
            description: ticketData.description,
            priority: ticketData.priority === 'Critical' ? 'P1' : ticketData.priority === 'High' ? 'P2' : ticketData.priority === 'Medium' ? 'P3' : 'P4',
            category: ticketData.category,
            source: 'ChatBot',
            attachments: uploadedFiles,
          } as any);

          if (res?.ticket?.ref || res?.ticket?.number) {
            assignedRef = res.ticket.ref || res.ticket.number;
          }
        } catch (err) {
          console.error('AIChatWidget createTicket error:', err);
        }
      }

      const refLabel = assignedRef ? `**${assignedRef}** ` : '';
      appendAiMessage(`✅ Ticket ${refLabel}has been created successfully!\n\nIt has been logged in the system and a technician will be assigned shortly.`);
      setTimeout(() => {
        appendAiMessage('Is there anything else I can help you with today?', ['Check Ticket Status', 'Create New Ticket']);
        setFlowState('idle');
      }, 3000);
    }
    else if (flowState === 'preview' && text === 'Edit Details') {
      setFlowState('category');
      appendAiMessage('Let\'s start over. What is the category of your issue?', ['Hardware', 'Software', 'Network', 'Access/Login', 'Other']);
    }
  };

  const formatTime = (d: Date) => {
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const renderTextWithMarkdown = (text: string) => {
    const parts = text.split(/(\*\*.*?\*\*)/g);
    return parts.map((part, index) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={index} className="font-semibold text-foreground/90">{part.slice(2, -2)}</strong>;
      }
      return <span key={index}>{part}</span>;
    });
  };

  if (!isOpen) {
    return (
      <button 
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 h-14 w-14 rounded-full bg-primary text-primary-foreground shadow-xl shadow-primary/30 flex items-center justify-center hover:scale-105 transition-transform z-50 animate-in fade-in zoom-in duration-300"
      >
        <MessageSquare className="h-6 w-6" />
      </button>
    );
  }

  return (
    <div className={cn(
      "fixed z-50 flex flex-col overflow-hidden bg-card border border-border shadow-2xl transition-all duration-300 ease-in-out ring-1 ring-black/5 dark:ring-white/10",
      isMaximized 
        ? "bottom-0 right-0 h-screen w-screen sm:bottom-6 sm:right-6 sm:h-[calc(100vh-48px)] sm:w-[800px] sm:rounded-2xl" 
        : "bottom-6 right-6 h-[600px] w-[380px] rounded-3xl"
    )}>
      <div className="flex items-center justify-between bg-primary px-4 py-3.5 text-primary-foreground relative shadow-sm">
        <div className="flex items-center gap-3 relative z-10">
          <div className="relative flex h-10 w-10 items-center justify-center rounded-full bg-white/20 shadow-inner">
            {isGuest ? <Bot className="h-6 w-6" /> : <span className="font-semibold text-lg uppercase">{userName.charAt(0)}</span>}
            <div className="absolute bottom-0 right-0 h-3 w-3 rounded-full border-2 border-primary bg-emerald-400"></div>
          </div>
          <div>
            <h3 className="font-semibold text-sm flex items-center gap-1.5">
              {isGuest ? 'Support Assistant' : userName}
            </h3>
            <p className="text-[10px] text-primary-foreground/80 opacity-90 flex items-center gap-1">
              🟢 Online • Ready to help
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1 relative z-10">
          <button onClick={() => setIsMaximized(!isMaximized)} className="p-2 hover:bg-white/20 rounded-lg transition-colors">
            {isMaximized ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
          </button>
          <button onClick={() => setIsOpen(false)} className="p-2 hover:bg-white/20 rounded-lg transition-colors">
            <ChevronDown className="h-5 w-5" />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 bg-muted/20 space-y-4">
        {messages.map((msg, idx) => (
          <div key={msg.id} className={cn("flex w-full", msg.sender === 'user' ? "justify-end" : "justify-start")}>
            <div className={cn("flex gap-2 max-w-[85%]", msg.sender === 'user' ? "flex-row-reverse" : "flex-row")}>
              
              {msg.sender === 'ai' && (
                <div className="flex-shrink-0 h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center mt-1">
                  <Bot className="h-4 w-4 text-primary" />
                </div>
              )}
              
              <div className="flex flex-col gap-1 max-w-full">
                <div className={cn(
                  "px-4 py-2.5 rounded-2xl text-sm whitespace-pre-wrap shadow-sm transition-all duration-300",
                  msg.sender === 'user' 
                    ? "bg-primary text-primary-foreground rounded-tr-sm" 
                    : "bg-background border border-border text-foreground rounded-tl-sm"
                )}>
                  {msg.isTyping ? (
                    <div className="flex items-center gap-1 h-5 px-1">
                      <div className="w-1.5 h-1.5 bg-primary/40 rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                      <div className="w-1.5 h-1.5 bg-primary/40 rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                      <div className="w-1.5 h-1.5 bg-primary/40 rounded-full animate-bounce"></div>
                    </div>
                  ) : (
                    renderTextWithMarkdown(msg.text)
                  )}
                </div>
                
                {!msg.isTyping && (
                  <div className={cn("text-[10px] text-muted-foreground px-1", msg.sender === 'user' ? "text-right" : "text-left")}>
                    {formatTime(msg.timestamp)}
                  </div>
                )}

                {msg.options && !msg.isTyping && idx === messages.length - 1 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {msg.options.map(opt => (
                      <button 
                        key={opt}
                        onClick={() => {
                          if (opt === 'Upload File') {
                            fileInputRef.current?.click();
                          } else {
                            handleSend(opt);
                          }
                        }}
                        className="text-xs bg-background text-primary hover:bg-primary hover:text-primary-foreground border border-primary/20 hover:border-transparent px-3.5 py-1.5 rounded-full transition-all duration-300 whitespace-nowrap"
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      <div className="p-3 border-t border-border bg-card">
        <form 
          onSubmit={(e) => { e.preventDefault(); handleSend(); }}
          className="flex items-center gap-2 rounded-full border border-input bg-background px-3 py-1.5 transition-all focus-within:ring-2 focus-within:ring-primary/20"
        >
          <input 
            type="text" 
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder="Type your message..."
            className="flex-1 bg-transparent text-sm outline-none disabled:opacity-50"
            disabled={messages[messages.length - 1]?.isTyping}
          />
          <input 
            type="file" 
            ref={fileInputRef} 
            className="hidden" 
            multiple
            onChange={handleFileUpload} 
            disabled={messages[messages.length - 1]?.isTyping}
          />
          <button 
            type="button" 
            onClick={() => fileInputRef.current?.click()}
            disabled={messages[messages.length - 1]?.isTyping}
            className="p-2 text-muted-foreground hover:text-foreground transition-colors rounded-full hover:bg-muted disabled:opacity-50"
          >
            <Paperclip className="h-4 w-4" />
          </button>
          <button 
            type="submit" 
            disabled={!inputValue.trim() || messages[messages.length - 1]?.isTyping}
            className="p-2 bg-primary text-primary-foreground rounded-full hover:opacity-90 transition-all duration-300 disabled:opacity-50"
          >
            <Send className="h-4 w-4" />
          </button>
        </form>
      </div>
    </div>
  );
}
